import {
  type CustomCell,
  type CustomRenderer,
  getMiddleCenterBias,
  GridCellKind,
} from "@glideapps/glide-data-grid";

export const stringToHue = (str: string): number => {
  let hash = 0;

  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);

    hash = (hash << 5) - hash + char;
    hash &= hash; // Convert to 32bit integer
  }

  return hash % 360;
};

/**
 * Canvas-pill palette — mirrors the `.chip-status` CSS in index.css so the
 * datagrid (canvas) and DOM-rendered Pill components share one visual system:
 * dark text on a light tint with a matching border, plus a colored dot.
 *
 * Hue is bucketed into the same semantic ranges the DOM Pill exposes via
 * PillStatusType (success / warning / error / info / attention / neutral).
 */
export const hueToPillColorLight = (hue: number): PillColor => {
  const normalized = ((hue % 360) + 360) % 360;

  // Neutral / gray (hue === 0 is the explicit "no color" sentinel)
  if (hue === 0) {
    return { base: "#f3f4f6", border: "#e5e7eb", text: "#374151", dot: "#9ca3af" };
  }
  // Red — error / rejected
  if (normalized >= 340 || normalized < 25) {
    return { base: "#fef2f2", border: "#fecaca", text: "#991b1b", dot: "#dc2626" };
  }
  // Orange — attention
  if (normalized < 50) {
    return { base: "#fff7ed", border: "#fed7aa", text: "#9a3412", dot: "#ea580c" };
  }
  // Amber — warning / draft / pending
  if (normalized < 90) {
    return { base: "#fffbeb", border: "#fde68a", text: "#92400e", dot: "#d97706" };
  }
  // Green — success / approved / active
  if (normalized < 180) {
    return { base: "#ecfdf5", border: "#a7f3d0", text: "#065f46", dot: "#10b981" };
  }
  // Blue — info
  if (normalized < 270) {
    return { base: "#eff6ff", border: "#bfdbfe", text: "#1e40af", dot: "#3b82f6" };
  }
  // Violet / indigo — type-ish, also covers the magenta tail
  return { base: "#eef2ff", border: "#e0e7ff", text: "#3730a3", dot: "#6366f1" };
};

export const hueToPillColorDark = (hue: number): PillColor => {
  const normalized = ((hue % 360) + 360) % 360;

  if (hue === 0) {
    return { base: "#1f2937", border: "#374151", text: "#e5e7eb", dot: "#9ca3af" };
  }
  if (normalized >= 340 || normalized < 25) {
    return { base: "#3f1d1d", border: "#7f1d1d", text: "#fecaca", dot: "#f87171" };
  }
  if (normalized < 50) {
    return { base: "#3a2410", border: "#7c2d12", text: "#fed7aa", dot: "#fb923c" };
  }
  if (normalized < 90) {
    return { base: "#3a2e0a", border: "#78350f", text: "#fde68a", dot: "#fbbf24" };
  }
  if (normalized < 180) {
    return { base: "#0f2a1f", border: "#065f46", text: "#a7f3d0", dot: "#34d399" };
  }
  if (normalized < 270) {
    return { base: "#172554", border: "#1e3a8a", text: "#bfdbfe", dot: "#60a5fa" };
  }
  return { base: "#1e1b4b", border: "#312e81", text: "#c7d2fe", dot: "#818cf8" };
};

export interface PillColor {
  readonly base: string;
  readonly border: string;
  readonly text: string;
  readonly dot?: string;
}
interface PillCellProps {
  readonly kind: "auto-tags-cell";
  readonly value: string;
  readonly color: {
    readonly text: string;
    readonly border: string;
    readonly base: string;
    readonly dot?: string;
  };
}

export type PillCell = CustomCell<PillCellProps>;

export const pillCellRenderer = (): CustomRenderer<PillCell> => ({
  kind: GridCellKind.Custom,
  isMatch: (c): c is PillCell => (c.data as any).kind === "auto-tags-cell",
  draw: (args, cell) => {
    const label = cell.data.value;
    const { rect, ctx, theme } = args;
    const { x, y, height } = rect;
    const { base, border, text, dot } = cell.data.color;

    // 600 weight matches `.chip-status` in index.css
    ctx.font = `600 12px ${theme.fontFamily}`;

    const textMetrics = ctx.measureText(label);
    const textWidth = textMetrics.width;
    const textHeight = textMetrics.fontBoundingBoxAscent + textMetrics.fontBoundingBoxDescent;

    const paddingX = 10;
    const dotRadius = 3;
    const dotGap = 6;
    const hasDot = !!dot;
    const dotBlockWidth = hasDot ? dotRadius * 2 + dotGap : 0;

    const tileWidth = textWidth + 2 * paddingX + dotBlockWidth;
    const tileHeight = textHeight * 1.35;
    const tileX = x + 10;
    const tileY = y + height / 2 - tileHeight / 2;

    if ("roundRect" in ctx) {
      ctx.fillStyle = base;
      ctx.strokeStyle = border;
      ctx.beginPath();
      ctx.roundRect(tileX, tileY, tileWidth, tileHeight, 999);
      ctx.fill();
      ctx.stroke();
    }

    let textStartX = tileX + paddingX;

    if (hasDot) {
      const dotCx = tileX + paddingX + dotRadius;
      const dotCy = y + height / 2;
      ctx.fillStyle = dot!;
      ctx.beginPath();
      ctx.arc(dotCx, dotCy, dotRadius, 0, Math.PI * 2);
      ctx.fill();
      textStartX = dotCx + dotRadius + dotGap;
    }

    ctx.fillStyle = text;
    ctx.fillText(label, textStartX, y + height / 2 + getMiddleCenterBias(ctx, theme));

    return true;
  },
});
